import type { SalesHierarchyNode } from "../types"
import type { TimePeriod } from "../types"
import { generateSalesSourceData } from "./data_generator"
import { hierarchySemanticPathKey } from "./normalization"
import { getOpportunity } from "./opportunities"

/** Mirrors create_sales_source_dialog growth shape (minimal for collision checks). */
export interface SalesSourceCollisionFormShape {
  classOfTrade: string
  customer: string
  planningBySku: boolean | null
  isOpportunity: boolean
  growthExpectations: Array<{
    productLineName: string
    productSku?: string
    skuName?: string
  }>
}

export interface SalesSourceCollisionAnalysis {
  needs_confirmation: boolean
  title: string
  body: string
}

function collectLeaves(roots: SalesHierarchyNode[]): SalesHierarchyNode[] {
  const out: SalesHierarchyNode[] = []
  function walk(node: SalesHierarchyNode): void {
    const ch = node.children
    if (Array.isArray(ch) && ch.length > 0) {
      ;(ch as SalesHierarchyNode[]).forEach((c: SalesHierarchyNode) => walk(c))
      return
    }
    out.push(node)
  }
  roots.forEach(walk)
  return out
}

/**
 * Detects overlap with existing base forecast paths (semantic keys).
 * Standard sources: additive merge in normalization — warn before save.
 * Opportunities: separate scenario rows after splice changes — informational when a base row already exists at the same slot.
 */
export function analyzeSalesSourceCollision(
  existing_roots: SalesHierarchyNode[],
  form_data: SalesSourceCollisionFormShape,
  periods: TimePeriod[]
): SalesSourceCollisionAnalysis {
  if (!form_data.customer?.trim() || form_data.planningBySku === null || form_data.growthExpectations.length === 0) {
    return { needs_confirmation: false, title: "", body: "" }
  }

  const proposed_roots = generateSalesSourceData(
    {
      classOfTrade: form_data.classOfTrade,
      customer: form_data.customer,
      routeToMarket: "",
      customerIsOther: false,
      planningBySku: form_data.planningBySku,
      isOpportunity: form_data.isOpportunity,
      opportunityName: form_data.isOpportunity ? "collision-check" : "",
      opportunityLikelihood: 50,
      growthExpectations: form_data.growthExpectations.map((g, i) => ({
        id: `g_${i}`,
        name: form_data.customer || `line_${i}`,
        initialDoors: 1,
        startMonth: periods[0]?.period_key ?? "2026-01",
        storeCountGrowthMultiplier: 0.02,
        productLineName: g.productLineName,
        initialCasesPerMonth: 1,
        costPerCase: 1,
        sameStoreSalesMultiplier: 0.03,
        productSku: g.productSku,
        skuName: g.skuName,
      })),
    },
    periods
  ).nodes

  const proposed_leaves = collectLeaves(proposed_roots)
  const existing_leaves = collectLeaves(existing_roots).filter((n: SalesHierarchyNode) => !getOpportunity(n))

  const existing_keys = new Set(
    existing_leaves.map((n: SalesHierarchyNode) => hierarchySemanticPathKey(n, existing_roots))
  )

  const matching = proposed_leaves.filter((pl: SalesHierarchyNode) =>
    existing_keys.has(hierarchySemanticPathKey(pl, proposed_roots))
  )

  if (matching.length === 0) {
    return { needs_confirmation: false, title: "", body: "" }
  }

  const sample = matching.map((n: SalesHierarchyNode) => n.name).join(", ")

  if (form_data.isOpportunity) {
    return {
      needs_confirmation: true,
      title: "Existing forecast at this product slot",
      body:
        `There is already a base forecast row for the same class of trade / customer / product path (${sample}). ` +
        `This opportunity will be added as a separate labeled row under that customer so your existing numbers stay intact. ` +
        `You can edit the original row directly if you meant to change the live forecast instead.`,
    }
  }

  return {
    needs_confirmation: true,
    title: "This path already exists in your forecast",
    body:
      `Saving will add volumes to the existing row(s) for ${sample} (same hierarchy path). ` +
      `The tool combines duplicate paths by summing dollars and cases by period — it does not replace the row. ` +
      `To change the existing forecast instead, cancel and edit that row in the grid.`,
  }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("analyzeSalesSourceCollision", () => {
    it("detects additive-merge collision for matching standard paths", () => {
      const periods = [{ year: 2026, month: 1, period_key: "2026-01" }]
      const form = {
        classOfTrade: "",
        customer: "Acme",
        planningBySku: false as const,
        isOpportunity: false,
        growthExpectations: [{ productLineName: "Bars" }],
      }
      const existing_roots = generateSalesSourceData(
        {
          classOfTrade: "",
          customer: "Acme",
          routeToMarket: "",
          customerIsOther: false,
          planningBySku: false,
          isOpportunity: false,
          growthExpectations: [
            {
              id: "g1",
              name: "",
              initialDoors: 1,
              startMonth: "2026-01",
              storeCountGrowthMultiplier: 0.02,
              productLineName: "Bars",
              initialCasesPerMonth: 10,
              costPerCase: 10,
              sameStoreSalesMultiplier: 0.03,
            },
          ],
        },
        periods
      ).nodes

      const analysis = analyzeSalesSourceCollision(existing_roots, form, periods)
      expect(analysis.needs_confirmation).toBe(true)
      expect(analysis.title.length).toBeGreaterThan(0)
    })
  })
}
